import { useEffect, useState, useRef } from 'react'
import { useWallet } from '@solana/wallet-adapter-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../services/api'
import { toast } from 'sonner'
import bs58 from 'bs58'

export function useAuth() {
  const { publicKey, signMessage, connected, disconnect, select, wallets, connect: adapterConnect } = useWallet()
  const [isAuthenticating, setIsAuthenticating] = useState(false)
  const queryClient = useQueryClient()
  const connectionAttemptRef = useRef<{ walletAddress: string | null; attempted: boolean }>({ walletAddress: null, attempted: false })

  const walletAddress = publicKey?.toBase58()

  // Query current user
  const { data: userResponse, isLoading: isLoadingUser } = useQuery({
    queryKey: ['currentUser'],
    queryFn: () => api.getCurrentUser(),
    enabled: api.isAuthenticated(), // Don't require wallet to be connected for password auth
    retry: false,
  })

  const user = userResponse?.data

  // Login mutation
  const loginMutation = useMutation({
    mutationFn: async () => {
      if (!publicKey || !signMessage) {
        throw new Error('Wallet not connected')
      }

      const walletAddr = publicKey.toBase58()

      // Step 1: Get nonce
      const nonceResponse = await api.getNonce(walletAddr)
      const { message } = nonceResponse.data

      // Step 2: Sign message
      const messageBytes = new TextEncoder().encode(message)
      const signatureBytes = await signMessage(messageBytes)
      const signature = bs58.encode(signatureBytes)

      // Step 3: Login
      const loginResponse = await api.login(walletAddr, signature)

      return loginResponse
    },
    onSuccess: () => {
      toast.success('Successfully authenticated!')
      queryClient.invalidateQueries({ queryKey: ['currentUser'] })
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Authentication failed')
      console.error('Login error:', error)
    },
  })

  // Logout function
  const logout = async () => {
    api.logout()
    queryClient.clear()
    
    // Set disconnect flag first
    localStorage.setItem('wallet_disconnected', 'true')
    
    if (connected) {
      await disconnect()
      
      // Also disconnect from Phantom directly if possible
      try {
        // @ts-ignore
        const phantom = window.phantom?.solana
        if (phantom?.isPhantom && phantom?.isConnected) {
          await phantom.disconnect()
        }
      } catch (err) {
        // Ignore errors from direct disconnect
      }
    }
    
    toast.success('Logged out successfully')
  }

  // Auto-connect to user's saved wallet on auth/init
  useEffect(() => {
    const autoConnectUserWallet = async () => {
      // Only check if user is authenticated
      if (!api.isAuthenticated()) {
        return
      }

      // Don't auto-connect if user explicitly disconnected
      const disconnectFlag = localStorage.getItem('wallet_disconnected')
      if (disconnectFlag === 'true') {
        // If disconnect flag is set, ensure wallet is disconnected
        if (connected) {
          await disconnect()
          // Also disconnect from Phantom directly
          try {
            // @ts-ignore
            const phantom = window.phantom?.solana
            if (phantom?.isPhantom && phantom?.isConnected) {
              await phantom.disconnect()
            }
          } catch (err) {
            // Ignore errors
          }
        }
        return
      }

      // If user has no saved wallet, don't auto-connect
      if (!user?.walletAddress) {
        return
      }

      const userWalletAddress = user.walletAddress

      // Check Phantom wallet
      // @ts-ignore
      const phantom = window.phantom?.solana
      
      if (!phantom?.isPhantom) {
        // Phantom not installed, can't auto-connect
        return
      }

      // If Phantom is already connected, check if it matches user's wallet
      if (phantom.isConnected && phantom.publicKey) {
        const phantomAddress = phantom.publicKey.toString()
        
        if (userWalletAddress === phantomAddress) {
          // Wallet matches - already connected, nothing to do
          return
        } else {
          // Connected wallet doesn't match user's wallet - disconnect it
          await disconnect()
          try {
            await phantom.disconnect()
          } catch (err) {
            // Ignore errors
          }
        }
      }

      // Try to auto-connect to user's saved wallet
      // Phantom will show wallet selection if multiple wallets exist
      if (!connected && !phantom.isConnected) {
          try {
          // Try silent connect first (if previously trusted)
          const response = await phantom.connect({ onlyIfTrusted: true })
          if (response?.publicKey) {
            const connectedAddress = response.publicKey.toString()
            
            // Check if connected wallet matches user's wallet
            if (connectedAddress === userWalletAddress) {
              // Perfect match - wallet is now connected
              // Sync with adapter
              try {
                const phantomWallet = wallets.find(w => w.adapter.name === 'Phantom')
                if (phantomWallet && !connected) {
                  select(phantomWallet.adapter.name)
                  await new Promise(resolve => setTimeout(resolve, 300))
                  try {
                    await adapterConnect()
                  } catch (err) {
                    // Adapter connect might fail if already connected via direct API
                  }
                }
              } catch (err) {
                // Ignore adapter sync errors
              }
            } else {
              // Connected to wrong wallet - disconnect
              await phantom.disconnect()
            }
          }
        } catch (err) {
          // Silent connect failed (not trusted or user needs to select wallet)
          // Try regular connect - this will show Phantom's wallet selection UI
          try {
            const response = await phantom.connect()
            if (response?.publicKey) {
              const connectedAddress = response.publicKey.toString()
              
              // Check if connected wallet matches user's wallet
              if (connectedAddress === userWalletAddress) {
                // Perfect match - sync with adapter
                try {
                  const phantomWallet = wallets.find(w => w.adapter.name === 'Phantom')
                  if (phantomWallet && !connected) {
                    select(phantomWallet.adapter.name)
                    await new Promise(resolve => setTimeout(resolve, 300))
                    try {
                      await adapterConnect()
                    } catch (err) {
                      // Adapter connect might fail if already connected via direct API
          }
        }
                } catch (err) {
                  // Ignore adapter sync errors
                }
              } else {
                // User selected different wallet - disconnect and show message
                await phantom.disconnect()
                toast.error('Please select the wallet that matches your account')
              }
            }
          } catch (connectErr: any) {
            // User rejected or error occurred
            if (connectErr.code !== 4001) {
              // Not a user rejection, log it
              console.warn('Auto-connect failed:', connectErr)
            }
          }
        }
      }
    }

    // Run after a short delay to ensure user data is loaded
    const timeoutId = setTimeout(autoConnectUserWallet, 500)
    return () => clearTimeout(timeoutId)
  }, [api.isAuthenticated(), user?.walletAddress, connected, disconnect, select, wallets, adapterConnect])

  // Auto-authenticate when wallet connects OR connect wallet to existing account
  useEffect(() => {
    const autoAuth = async () => {
      // Don't auto-connect if user explicitly disconnected
      const disconnectFlag = localStorage.getItem('wallet_disconnected')
      if (disconnectFlag === 'true') {
        // Ensure wallet is disconnected if flag is set
        if (connected) {
          await disconnect()
          try {
            // @ts-ignore
            const phantom = window.phantom?.solana
            if (phantom?.isPhantom && phantom?.isConnected) {
              await phantom.disconnect()
            }
          } catch (err) {
            // Ignore errors
          }
        }
        return
      }

      if (connected && walletAddress && !isAuthenticating) {
        // Case 1: User not authenticated - try wallet login
        if (!api.isAuthenticated()) {
          setIsAuthenticating(true)
          try {
            await loginMutation.mutateAsync()
          } catch (error) {
            console.error('Auto-auth error:', error)
          } finally {
            setIsAuthenticating(false)
          }
        }
        // Case 2: User already authenticated (via email/password) but wallet not saved
        else if (user) {
          // Check if connected wallet matches user's wallet
          if (user.walletAddress && user.walletAddress !== walletAddress) {
            // Prevent repeated disconnection attempts
            if (connectionAttemptRef.current.walletAddress === walletAddress && connectionAttemptRef.current.attempted) {
              return
            }
            connectionAttemptRef.current = { walletAddress, attempted: true }
            
            // Wallet doesn't match - disconnect it
            await disconnect()
            try {
              // @ts-ignore
              const phantom = window.phantom?.solana
              if (phantom?.isPhantom && phantom?.isConnected) {
                await phantom.disconnect()
              }
            } catch (err) {
              // Ignore errors
            }
            // Only show toast once
            toast.error('This wallet belongs to another account. Please connect your wallet.')
            return
          }
          
          // User has no wallet - try to connect current wallet
          if (!user.walletAddress && walletAddress) {
            // Prevent repeated connection attempts for the same wallet
            if (connectionAttemptRef.current.walletAddress === walletAddress && connectionAttemptRef.current.attempted) {
              return
            }
            
            connectionAttemptRef.current = { walletAddress, attempted: true }
            setIsAuthenticating(true)
            try {
              await api.connectSolana(walletAddress)
              queryClient.invalidateQueries({ queryKey: ['currentUser'] })
              toast.success('Phantom wallet connected to your account!')
              // Reset attempt flag on success
              connectionAttemptRef.current = { walletAddress: null, attempted: false }
            } catch (error: any) {
              console.error('Connect wallet error:', error)
              // If wallet is already assigned to another account, disconnect it
              if (error.response?.data?.message?.includes('already') || 
                  error.response?.data?.message?.includes('another account')) {
                await disconnect()
                try {
                  // @ts-ignore
                  const phantom = window.phantom?.solana
                  if (phantom?.isPhantom && phantom?.isConnected) {
                    await phantom.disconnect()
                  }
                } catch (err) {
                  // Ignore errors
                }
                // Don't show toast repeatedly - only once
                if (!connectionAttemptRef.current.attempted || connectionAttemptRef.current.walletAddress !== walletAddress) {
                  toast.error('This wallet is already connected to another account')
                }
              } else if (!error.response?.data?.message?.includes('already connected')) {
                toast.error(error.response?.data?.message || 'Failed to connect wallet')
                // Reset on other errors so user can retry
                connectionAttemptRef.current = { walletAddress: null, attempted: false }
              }
            } finally {
              setIsAuthenticating(false)
            }
          }
        }
      }
    }

    autoAuth()
  }, [connected, walletAddress, user?.walletAddress, disconnect])

  return {
    user,
    isAuthenticated: api.isAuthenticated(), // Allow password auth without wallet connection
    isLoading: isLoadingUser || loginMutation.isPending || isAuthenticating,
    login: loginMutation.mutate,
    logout,
    walletAddress,
  }
}
